import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  teamLicensesActions, teamsActions, teamMembersActions,
} from '../redux/actions';
import api from '../api';
import history from '../history';
import Pagination from '../components/Pagination';
import {
  Table, TableHead, TableRow, TableBody, TableCell,
} from '../components/CustomTable';
import { CardLoader } from '../components/Loader';
import Page from '../components/Page';
import DashboardHead from '../components/DashboardHead';
import NewTooltip from '../components/NewTooltip';
import TeamForm from '../components/TeamForm';
import BrowserView from '../components/BrowserView';
import MobileUnsupported from '../components/MobileUnsupported';
import TabletUnsupported from '../components/TabletUnsupported';
import TeamCreateModal from '../components/TeamCreateModal';
import useError from '../utils/HookErrorUtil';
import TextUtil from '../utils/TextUtil';
import Warning from '../components/Warning';
import TruncatedBadgeList from '../components/TruncatedBadgeList';
import {
  CAN_VIEW_RESEARCH_TEAMS,
  CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
  TEAM_TYPE_RESEARCH,
} from '../constants';

const TEXT_MAX_LENGTH = 40;

/**
 * Page for viewing a single institutional team
 */
export function InstitutionalTeamPage(props) {
  const { id } = useParams();

  const { handleCatched } = useError();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const tms = useSelector((state) => state.teams);

  const teams = useMemo(() => Object.values(tms), [tms]);
  const team = useMemo(() => teams.find((tm) => tm.id === Number(id)), [teams, id]);

  const dispatch = useDispatch();

  const fetchTeams = async (data) => dispatch(teamsActions.list(data, { pagination: 'no' }));
  const fetchTeamLicenses = async () => dispatch(teamLicensesActions.list(
    {}, { pagination: 'no', useCache: true },
  ));
  const fetchTeamMembers = async (teamId) => dispatch(teamMembersActions.list(
    { team: teamId }, { pagination: 'no' },
  ));

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTeams({ id }),
        fetchTeamLicenses(),
      ]);
      await fetchTeamMembers(Number(id));
    } catch (error) {
      handleCatched(props, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const returnToTeamsPage = () => {
    history.push('/dashboard/institutional-teams');
  };

  return (
    <Page
      {...props}
      title={t('user:nav.institutional-teams')}
    >
      <DashboardHead
        {...props}
        title={t('user:nav.institutional-teams')}
        breadcrumdPrevious={[]}
      />
      <div className="dashboard-content mb-2">
        <div className="card bg-white table-responsive contains-loader card-shadow p-4 mt-3 mobile-card overflow-x-visible">
          {loading && <CardLoader />}
          {team && (
            <>
              <div className="mb-3">
                <h4 className="font-weight-normal list-title mb-0">
                  {team.name}
                </h4>
              </div>
              <div className="pt-3">
                <TeamForm
                  team={team}
                  onDeleteTeam={returnToTeamsPage}
                  institutionalTeamsMode
                  {...props}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

InstitutionalTeamPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape().isRequired,
  }).isRequired,
};

/**
 * Page listing institutional teams for admin team members
 * (Users who have research teams permissions via their admin team membership)
 */
function InstitutionalTeams(props) {
  const { i18n, embedded } = props;

  const { handleCatched } = useError();
  const { t } = useTranslation();

  const authUser = useSelector((state) => state.auth.authUser);
  const teamLicenses = useSelector((state) => state.teamLicenses);

  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [page, setPage] = useState(1);
  const [teamCount, setTeamCount] = useState(0);
  const [pageSize, setPageSize] = useState(1);
  // Use local state for teams to avoid overwriting Redux state
  const [teams, setTeams] = useState([]);

  // Organizations for which user has admin permissions
  const [organizationIds, setOrganizationIds] = useState([]);
  const [organizationNames, setOrganizationNames] = useState({});
  const [canCreate, setCanCreate] = useState(false);

  const dispatch = useDispatch();

  const fetchTeamLicenses = async () => dispatch(teamLicensesActions.list());

  /**
   * Fetch admin team memberships to get organization IDs
   */
  const fetchAdminTeamMemberships = async () => {
    try {
      const memberships = await api.list(
        'admin-team-members',
        { user: authUser.id },
        { pagination: 'no' },
      );

      // Filter memberships that have research teams permissions
      const orgsWithPermissions = memberships
        .filter((membership) => {
          const permissions = membership.team_permissions || [];
          const permissionNames = permissions.map((p) => p.name);
          return permissionNames.includes(CAN_VIEW_RESEARCH_TEAMS)
            || permissionNames.includes(CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS);
        });

      const orgIds = orgsWithPermissions
        .map((membership) => membership.team_details?.owner)
        .filter(Boolean);

      // Store organization names for display
      const orgNames = {};
      orgsWithPermissions.forEach((membership) => {
        const ownerId = membership.team_details?.owner;
        if (ownerId) {
          // eslint-disable-next-line camelcase
          const displayName = membership.team_details?.owner_display_name;
          orgNames[ownerId] = displayName || `Organization ${ownerId}`;
        }
      });

      const hasCreatePermission = orgsWithPermissions.some((membership) => {
        const permissions = membership.team_permissions || [];
        return permissions.some((p) => p.name === CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS);
      });

      setOrganizationIds(orgIds);
      setOrganizationNames(orgNames);
      setCanCreate(hasCreatePermission);
      return orgIds;
    } catch (error) {
      handleCatched(props, error, false);
      return [];
    }
  };

  const fetchPaginatedTeams = async (currentPage, orgIds = organizationIds) => {
    if (orgIds.length === 0) {
      setReady(true);
      return;
    }

    try {
      setLoading(true);

      // Use api.list directly to avoid overwriting Redux teams state
      const res = await api.list(
        'teams',
        {
          page: currentPage,
          ordering: '-licence__pk',
          annotate_members_count: true,
          annotate_projects: true,
          annotate_first_leader: true,
          owner__in: orgIds.join(','),
          type: TEAM_TYPE_RESEARCH,
        },
        { pagination: 'short' },
      );

      // Sort teams: TExpert > TAdvanced > TPro
      const sortedTeams = res.results.sort((a, b) => (a.licence > b.licence ? -1 : 1));
      setTeams(sortedTeams);
      setPage(currentPage);
      setTeamCount(res.count);
      setPageSize(Math.max(res.results.length, pageSize));
    } catch (error) {
      handleCatched(props, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const orgIds = await fetchAdminTeamMemberships();
      await Promise.all([
        fetchPaginatedTeams(1, orgIds),
        fetchTeamLicenses(),
      ]);
      setReady(true);
    } catch (error) {
      handleCatched(props, error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const cellLinkProps = {
    className: 'text-center align-middle cursor-default',
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); },
  };

  const content = (
    <div className={embedded ? '' : 'dashboard-content mb-2'}>
      <div className={`${embedded ? '' : 'card bg-white card-shadow p-4 mt-3 mobile-card'} table-responsive contains-loader overflow-x-visible`}>
        <BrowserView>
          <h4 className="font-weight-normal list-title">
            {t('user:institutional-teams.title')}
          </h4>
          <p className="text-muted mb-4">
            {t('user:institutional-teams.description')}
          </p>
          <div className="row justify-content-between align-items-center mt-3">
            <div className="col-12 col-md-auto">
              <div className="row align-items-center">
                <div className="col-auto text-bigger">
                  {teamCount}
                    &nbsp;
                  {t('common:dashboard.team', { count: teamCount })}
                </div>
              </div>
            </div>
            {canCreate && (
            <div className="col-12 col-md-auto">
              <TeamCreateModal
                i18n={i18n}
                institutionalTeamsMode
                organizationIds={organizationIds}
              >
                <Button
                  color="newturquoise-1"
                >
                  <FontAwesomeIcon
                    icon={['fal', 'plus']}
                    transform="grow-3"
                    className="mr-2"
                  />
                  <span className="ml-1">
                    {t('user:team.add')}
                  </span>
                </Button>
              </TeamCreateModal>
            </div>
            )}
          </div>
          <Table extraClassName="mt-3 mb-0">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('user:team.name')}
                </TableCell>
                <TableCell>
                  {t('user:team.discipline')}
                </TableCell>
                <TableCell>
                  {t('user:team.title.owner')}
                </TableCell>
                <TableCell>
                  {t('user:team.role.leader')}
                </TableCell>
                <TableCell>
                  {t('user:team.members')}
                </TableCell>
                <TableCell>
                  {t('user:team.projects')}
                </TableCell>
                <TableCell>
                  {t('user:team.license')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ready && !loading && teams.length > 0 ? (
                teams.map((team) => {
                  const license = teamLicenses[team.licence];
                  const { name } = team;
                  const truncatedName = TextUtil.truncate(name, TEXT_MAX_LENGTH);
                  const teamProjects = team.project_names || [];
                  const projectsCount = team.projects_count || 0;
                  return (
                    <TableRow
                      key={team.id}
                      onClick={() => {
                        history.push(`/dashboard/institutional-team/${team.id}`);
                      }}
                    >
                      <TableCell>
                        <NewTooltip
                          content={name}
                          disabled={name === truncatedName}
                        >
                          <span className="font-weight-semibold">
                            {truncatedName}
                          </span>
                        </NewTooltip>
                      </TableCell>
                      <TableCell>
                        <TruncatedBadgeList
                          items={team.specialties.map((specialty) => t(`specialty:${specialty}`))}
                          badgeColor="badge-newyellow-2"
                          maxDisplayedItems={2}
                        />
                      </TableCell>
                      <TableCell>
                        {team.owner?.label || team.owner_display_name}
                      </TableCell>
                      <TableCell>
                        {team.first_leader ? (
                          <span>
                            {team.first_leader}
                          </span>
                        ) : (
                          <Warning
                            placement="top"
                            tooltipTheme="dark"
                          >
                            {t('user:team.no-team-leader-tooltip')}
                          </Warning>
                        )}
                      </TableCell>
                      <TableCell>
                        {team.members_count || 0}
                      </TableCell>
                      <TableCell
                        {...cellLinkProps}
                      >
                        {projectsCount > 0 ? (
                          <Link
                            to="/dashboard/projects"
                          >
                            <NewTooltip
                              content={teamProjects.join(', ')}
                              disabled={teamProjects.length === 0}
                            >
                              <span>
                                {projectsCount}
                              </span>
                            </NewTooltip>
                            <NewTooltip
                              content={t('user:team.view-projects')}
                            >
                              <FontAwesomeIcon
                                icon={['far', 'external-link']}
                                className="ml-2 text-gray"
                                transform="shrink-3"
                              />
                            </NewTooltip>
                          </Link>
                        ) : (
                          <span>
                            {projectsCount}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        {...cellLinkProps}
                      >
                        {license ? (
                          <span>
                            {t(`user:licenses.team-${license.key}`)}
                          </span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan="7">
                    {loading || !ready ? (
                      <CardLoader />
                    ) : (
                      <div className="text-center py-3">
                        <span className="d-block mb-2">
                          {t('user:institutional-teams.no-teams')}
                        </span>
                        {organizationIds.length > 0 && (
                          <small className="text-muted">
                            {t('user:institutional-teams.accessible-organizations')}
                            {': '}
                            {organizationIds.map((id, index) => (
                              <span key={id}>
                                <strong>{organizationNames[id]}</strong>
                                {index < organizationIds.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </small>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {teamCount > 10 && (
          <div className="mt-2">
            <Pagination
              page={page}
              count={teamCount}
              pageSize={pageSize}
              action={fetchPaginatedTeams}
            />
          </div>
          )}
        </BrowserView>
        <TabletUnsupported />
        <MobileUnsupported>
          common:mobile-and-tablet-unsupported
        </MobileUnsupported>
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Page
      {...props}
      title={t('user:nav.institutional-teams')}
    >
      <DashboardHead
        {...props}
        title={t('user:nav.institutional-teams')}
        breadcrumdPrevious={[]}
      />
      {content}
    </Page>
  );
}

InstitutionalTeams.propTypes = {
  i18n: PropTypes.shape().isRequired,
  embedded: PropTypes.bool,
};

InstitutionalTeams.defaultProps = {
  embedded: false,
};

export default InstitutionalTeams;
